import { getStoredShopId, saveShopId } from './shopId';

/** query 字段：扫码店铺、会员邀请人和门店推广渠道。 */
export const REGISTER_CONTEXT_KEYS = ['id', 'referId', 'campaign'];

/** localStorage：邀请人在 URL 缺失时兜底（与扫码店铺一致） */
const REFER_STORAGE_KEY = 'taogo-refer-id';
const CAMPAIGN_STORAGE_KEY = 'taogo-registration-campaign';

export function saveReferToStorage(referId) {
  if (referId != null && referId !== '') {
    localStorage.setItem(REFER_STORAGE_KEY, String(referId));
  }
}

export function getStoredReferId() {
  return localStorage.getItem(REFER_STORAGE_KEY);
}

export function saveCampaignToStorage(campaign) {
  if (campaign != null && campaign !== '') {
    localStorage.setItem(CAMPAIGN_STORAGE_KEY, String(campaign));
  }
}

export function getStoredCampaign() {
  return localStorage.getItem(CAMPAIGN_STORAGE_KEY);
}

export function clearCampaignFromStorage() {
  localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
}

/**
 * JS 运行时尽早调用：在未发生任何 React 路由跳转前，
 * 把地址栏里的 id / referId 写入本地，避免服务端或后续跳转让 query 丢失后无法溯源。
 */
export function captureQrContextFromWindow() {
  if (typeof window === 'undefined') {
    return;
  }
  const p = new URLSearchParams(window.location.search);
  const id = p.get('id');
  if (id) {
    saveShopId(id);
  }
  const ref = p.get('referId');
  if (ref) {
    saveReferToStorage(ref);
  }
  const campaign = p.get('campaign');
  if (campaign) {
    saveCampaignToStorage(campaign);
  }
}

/**
 * URL 里没有 id/referId 时，用语义合并后的上下文（包含 localStorage 兜底）。
 */
export function mergeRegisterContextSearch(searchParamsInput) {
  const source =
    searchParamsInput instanceof URLSearchParams
      ? searchParamsInput
      : new URLSearchParams(searchParamsInput);
  const next = getRegisterContextQuery(source);

  const storedShop = getStoredShopId();
  if (!next.get('id') && storedShop) {
    next.set('id', storedShop);
  }
  const storedRef = getStoredReferId();
  if (!next.get('referId') && storedRef) {
    next.set('referId', storedRef);
  }
  const storedCampaign = getStoredCampaign();
  if (!next.get('campaign') && storedCampaign) {
    next.set('campaign', storedCampaign);
  }

  return next;
}

/**
 * 保留当前 URL 的全部 query（如 redirect），并确保 id/referId 已由 URL ∪ localStorage 合并写回。
 */
export function overlayMergedRegisterKeys(searchParams) {
  const out =
    searchParams instanceof URLSearchParams
      ? new URLSearchParams(searchParams.toString())
      : new URLSearchParams(searchParams);
  const merged = mergeRegisterContextSearch(searchParams);
  for (const key of REGISTER_CONTEXT_KEYS) {
    const v = merged.get(key);
    if (v) {
      out.set(key, v);
    }
  }
  return out;
}

/**
 * 地址栏缺少或不同于合并后的店铺/邀请信息时返回应替换的完整 query，否则返回 null。
 * （避免仅以整串 compare 触发因参数顺序导致的反复 replace。）
 */
export function overlayIfRegisterKeysStale(searchParams) {
  const src =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams);
  const next = overlayMergedRegisterKeys(src);
  for (const key of REGISTER_CONTEXT_KEYS) {
    const a = next.get(key) ?? '';
    const b = src.get(key) ?? '';
    if (a !== b) {
      return next;
    }
  }
  return null;
}

/** 仅提取需要在登录页与注册页之间保留的查询参数 */
export function getRegisterContextQuery(searchParams) {
  const next = new URLSearchParams();
  for (const key of REGISTER_CONTEXT_KEYS) {
    const v = searchParams.get(key);
    if (v) {
      next.set(key, v);
    }
  }
  return next;
}

/** 向已有 URLSearchParams 追加扫码/邀请上下文（来自第一段 searchParams） */
export function appendRegisterContext(searchParams, targetParams) {
  const out =
    targetParams instanceof URLSearchParams
      ? new URLSearchParams(targetParams)
      : new URLSearchParams(targetParams);
  for (const key of REGISTER_CONTEXT_KEYS) {
    const v = searchParams.get(key);
    if (v) {
      out.set(key, v);
    }
  }
  return out;
}
